import React, { useEffect } from 'react';
import Row from 'react-bootstrap/Row';
import { Modal, Button, Form, Col } from 'react-bootstrap';
import { ReadCursoDto, ReadEstagiarioEstagioDto, ReadInstituicaoEnsinoDto, ResponseEstagioDto } from '@/api.types';
import { PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import { TipoLotacaoEstagioList } from '@/shared/constants/enumlist';
import { UseFieldArrayAppend, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useForm } from 'react-hook-form';

interface AdicionarEstagioModalProps {
  show: boolean;
  handleHide: () => void;
  cursos: ReadCursoDto[];
  instituicoes: ReadInstituicaoEnsinoDto[];
  initialData: ReadEstagiarioEstagioDto;
  estagioIndex: number;
  appendEstagio: UseFieldArrayAppend<PersonFormDto, 'estagiario.estagios'>;
  setValue: UseFormSetValue<PersonFormDto>;
  getValues: UseFormGetValues<PersonFormDto>;
}

type EstagioArray =
  NonNullable<PersonFormDto['estagiario']> extends { estagios: infer E }
  ? E
  : ResponseEstagioDto[];

type EstagioFormValue = EstagioArray extends Array<infer Item> ? Item : ResponseEstagioDto;

const AdicionarEstagioModal: React.FC<AdicionarEstagioModalProps> = ({
  show,
  handleHide,
  cursos,
  instituicoes,
  initialData,
  estagioIndex,
  appendEstagio,
  setValue,
  getValues,
}) => {
  const { register, handleSubmit, reset } = useForm<ResponseEstagioDto>();

  const normalizeEstagioData = (input: ResponseEstagioDto): EstagioFormValue => ({
    ...input,
    tipo_estagio: Number(input.tipo_estagio) as EstagioFormValue['tipo_estagio'],
    curso: input.curso?.id ? { ...input.curso, id: Number(input.curso.id) } : input.curso,
    instituicao_ensino: input.instituicao_ensino?.id
      ? { ...input.instituicao_ensino, id: Number(input.instituicao_ensino.id) }
      : input.instituicao_ensino,
  }) as EstagioFormValue;

  const onSubmit = (data: ResponseEstagioDto) => {
    const normalizedData = normalizeEstagioData(data);

    if (estagioIndex >= 0) {
      const estagios = getValues('estagiario.estagios');
      const existingEstagio = estagios?.[estagioIndex] as ResponseEstagioDto | undefined;

      const updatedEstagio = {
        ...existingEstagio,
        ...normalizedData,
      } as ResponseEstagioDto;

      setValue(`estagiario.estagios.${estagioIndex}`, updatedEstagio as any, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      appendEstagio(normalizedData as any, { shouldFocus: false });
    }

    handleHide();
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  return (
    <>
      <Modal show={show} onHide={handleHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {initialData?.id ? 'Editar ' : 'Adicionar '} Estágio
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div role="form">
            <Row className="mb-5">
              <Form.Group as={Col} md="3" className="mb-3">
                <label htmlFor="tipo_estagio" className="form-label">
                  Tipo:
                </label>
                <select
                  id="tipo_estagio"
                  className="form-select"
                  {...register('tipo_estagio')}
                  required
                >
                  {TipoLotacaoEstagioList.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group as={Col} md="5" className="mb-3">
                <label htmlFor="instituicao_ensino" className="form-label">
                  Instituição Ensino:
                </label>
                <select
                  id="instituicao_ensino"
                  className="form-select"
                  {...register('instituicao_ensino.id')}
                  required
                >
                  {instituicoes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.razao_social}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group as={Col} md="4" className="mb-3">
                <label htmlFor="curso" className="form-label">
                  Curso:
                </label>
                <select
                  id="curso"
                  className="form-select"
                  {...register('curso.id')}
                  required
                >
                  {cursos.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nome}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group as={Col} md="3" className="mb-3">
                <label htmlFor="data_inicio_estagio" className="form-label">
                  Início do Estágio:
                </label>
                <input
                  type="date"
                  id="data_inicio_estagio"
                  className="form-control"
                  {...register('data_inicio_estagio')}
                  required
                />
              </Form.Group>
              <Form.Group as={Col} md="3" className="mb-3">
                <label htmlFor="data_fim_estagio" className="form-label">
                  Fim do Estágio:
                </label>
                <input
                  type="date"
                  id="data_fim_estagio"
                  className="form-control"
                  {...register('data_fim_estagio')}
                />
              </Form.Group>
              <Form.Group as={Col} md="3" className="mb-3">
                <label htmlFor="desligamento" className="form-label">
                  Desligamento:
                </label>
                <input
                  type="date"
                  id="desligamento"
                  className="form-control"
                  {...register('desligamento')}
                />
              </Form.Group>
              <Form.Group as={Col} md="3" className="mb-3">
                <label htmlFor="data_colacao_grau" className="form-label">
                  Colação de Grau:
                </label>
                <input
                  type="month"
                  id="data_colacao_grau"
                  className="form-control"
                  {...register('data_colacao_grau')}
                  required
                />
              </Form.Group>
              {/* <Form.Group as={Col} md="7" className="mb-3">
                <label htmlFor="email" className="form-label">
                  E-mail:
                </label>
                <input
                  type="email"
                  id="email"
                  className="form-control"
                  {...register('email')}
                />
              </Form.Group> */}
              <Form.Group as={Col} md="5" className="mt-4">
                <div className="form-check" title="Indica se o estagiário trabalha nos fins de semana" >
                  <input
                    id="fim_semana"
                    className="form-check-input"
                    title="Fim de Semana"
                    type="checkbox"
                    {...register('fim_semana')}
                  />
                  <label htmlFor="fim_semana" className="form-check-label">
                    Trabalha Final de Semana
                  </label>
                </div>
              </Form.Group>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleHide}>
                  Fechar
                </Button>
                <Button type="button" variant="success" onClick={handleSubmit(onSubmit)}>
                  Salvar
                </Button>
              </Modal.Footer>
            </Row>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdicionarEstagioModal;
